"use client";

import React from "react";
import { useParams } from "next/navigation";
import { Swiper, SwiperSlide } from "swiper/react";

import Link from "./link";

import useMoengageEventTracker from "@/hooks/use-moengage";

import EVENT_NAME from "@/hooks/use-moengage/event-name.json";
import EVENT_ATTRIBUTE from "@/hooks/use-moengage/event-attribute.json";

import { cn } from "@/lib/utils";
import { Text } from "./ui/typography";

function CategorySliderBar({
  categories,
  className,
  onChange,
  useLink = false,
  ...props
}) {
  const { category } = useParams();
  const trackMoengageEvent = useMoengageEventTracker();

  const [selectedCategory, setSelectedCategory] = React.useState(null);

  // Handle category change event
  const handleOnSelectGoalItemEvent = React.useCallback((event) => {
    setSelectedCategory(event.detail.id);
  }, []);

  // Listeners for select goal item event
  React.useEffect(() => {
    window.addEventListener("selectGoalItem", handleOnSelectGoalItemEvent);

    return () =>
      window.removeEventListener("selectGoalItem", handleOnSelectGoalItemEvent);
  }, [handleOnSelectGoalItemEvent]);

  // Handle category click event
  const handleOnCategoryClick = React.useCallback(
    (category) => () => {
      if (!useLink) {
        setSelectedCategory(category.id);
      }

      trackMoengageEvent(EVENT_NAME.HOMEPAGE_CONTAINER_CATEGORY_CLICKED, {
        [EVENT_ATTRIBUTE.POSITION]: "top",
        [EVENT_ATTRIBUTE.COURSE_CATEGORY]: category.title,
      });
    },
    [useLink, trackMoengageEvent]
  );

  // Set initial category based on props or state
  React.useEffect(() => {
    if (typeof onChange === "function" && selectedCategory) {
      onChange(selectedCategory);
    } else if (category) {
      setSelectedCategory(category);
    } else if (
      typeof handleOnCategoryClick !== "undefined" &&
      selectedCategory === null &&
      categories?.length
    ) {
      handleOnCategoryClick(categories[0])();
    }
  }, [category, selectedCategory, onChange, categories, handleOnCategoryClick]);

  // If no categories are provided, don't render anything
  if (!categories?.length) return null;

  return (
    <div className={cn("block w-full", className)} {...props}>
      <Swiper spaceBetween={28} slidesPerView="auto">
        {categories.map((category) => (
          <SwiperSlide
            key={category.slug}
            className={cn(
              "relative w-fit! h-8! text-grayscale-body *:cursor-pointer",
              "before:inline-block before:absolute before:bottom-0 before:left-0 before:w-full",
              "before:bg-primary before:h-0.75 before:rounded-t-sm before:translate-y-full",
              selectedCategory === category.id && "before:translate-y-0"
            )}
          >
            <Text
              data-goal={`goal-${category.id}`}
              as={useLink ? Link : "span"}
              href={useLink ? `/${category.slug}` : undefined}
              variant="small-tight"
              onClick={handleOnCategoryClick(category)}
            >
              {category.title}
            </Text>
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
}

export default CategorySliderBar;
